import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LogOut, LayoutDashboard, FileEdit, BarChart3, Users, Map, Euro, KeyRound, ChevronDown } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [pwdOpen, setPwdOpen] = useState(false);

  const isAdmin = user?.role === "admin";

  const collabNav = [
    { to: "/dashboard", label: "Mon espace", icon: LayoutDashboard },
    { to: "/rapport", label: "Rapport de la semaine", icon: FileEdit },
    { to: "/ventes", label: "Mes ventes", icon: Euro },
    { to: "/equipe-carte", label: "Carte équipe", icon: Map },
  ];
  const adminNav = [
    { to: "/admin", label: "Tableau de bord", icon: LayoutDashboard },
    { to: "/admin/ventes", label: "Ventes & CA", icon: Euro },
    { to: "/admin/stats", label: "Statistiques", icon: BarChart3 },
    { to: "/admin/carte", label: "Carte", icon: Map },
    { to: "/admin/rapports", label: "Rapports", icon: FileEdit },
    { to: "/admin/collaborateurs", label: "Collaborateurs", icon: Users },
  ];
  const nav = isAdmin ? adminNav : collabNav;

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b border-border bg-white/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to={isAdmin ? "/admin" : "/dashboard"} className="flex items-center group" data-testid="header-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_sales-tracker-1019/artifacts/0i835tsb_f293e0f8-0ee3-473a-b4f8-1de12450a9c6.png"
              alt="Immo Varois"
              className="h-12 w-auto object-contain"
            />
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {nav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  data-testid={`nav-${item.to.replace(/\//g, "-")}`}
                  className={`px-3 py-2 rounded-md text-sm flex items-center gap-2 transition-all duration-200 ${
                    active
                      ? "bg-primary/10 text-primary font-semibold"
                      : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>

          <div className="flex items-center gap-3">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="hidden sm:flex items-center gap-2 leading-tight rounded-md px-2 py-1 hover:bg-slate-100 transition-colors" data-testid="user-menu-trigger">
                  <div className="flex flex-col items-end">
                    <div className="text-sm font-semibold text-slate-900">{user?.name}</div>
                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                      {isAdmin ? "Administrateur" : "Négociateur"}
                    </div>
                  </div>
                  <ChevronDown className="h-4 w-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => setPwdOpen(true)} data-testid="menu-change-password">
                  <KeyRound className="h-4 w-4 mr-2" /> Changer mon mot de passe
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-700 focus:bg-red-50">
                  <LogOut className="h-4 w-4 mr-2" /> Se déconnecter
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              data-testid="header-logout-button"
              className="gap-2 sm:hidden"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Mobile nav */}
        <div className="md:hidden border-t border-border bg-white overflow-x-auto">
          <div className="flex px-3 gap-1">
            {nav.map((item) => {
              const Icon = item.icon;
              const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
              return (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={`px-3 py-2.5 text-xs flex items-center gap-1.5 border-b-2 ${
                    active ? "border-primary text-primary font-semibold" : "border-transparent text-slate-500"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" />
                  {item.label}
                </NavLink>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-8 py-8">{children}</main>
      <Toaster position="top-right" richColors />
      <ChangePasswordDialog open={pwdOpen} onClose={() => setPwdOpen(false)} />
    </div>
  );
}
